import { extractImageFrames, clampTo } from '../../tools';
import type { TencentModelConfig, StatusMapping } from '../types';

// 腾讯云点播 AIGC 生视频 (Kling)
// 创建任务: https://cloud.tencent.com/document/product/266/126239
// 查询任务: https://cloud.tencent.com/document/product/266/33431
// 数据结构: https://cloud.tencent.com/document/api/266/31773

/**
 * 腾讯云 DescribeTaskDetail 通用状态映射
 * 注意：Response.Status 为 FINISH 时任务不一定成功，
 * 需结合 AigcVideoTask.ErrCode 判断（非 0 表示失败）
 */
const TENCENT_AIGC_STATUS_MAPPING: StatusMapping = {
    taskIdPath: 'Response.TaskId',
    success: ['FINISH'],
    failed: ['FAIL'],
    processing: ['WAITING', 'PROCESSING'],
    statePath: 'Response.Status',
    videoUrlPath: 'Response.AigcVideoTask.Output.FileInfos[0].FileUrl',
    errorPath: 'Response.AigcVideoTask.Message',
    errCodePath: 'Response.AigcVideoTask.ErrCode',
};

export const klingConfigs: Record<string, TencentModelConfig> = {
    'kling-v3.0': {
        enableTranslation: false,
        statusMapping: TENCENT_AIGC_STATUS_MAPPING,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);

            // 腾讯云分辨率枚举值为大写: [480P, 720P, 1080P, 2K, 4K]
            const resolution = clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p').toUpperCase();
            const outputConfig = {
                StorageMode: 'Temporary',
                Duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                Resolution: resolution,
                // 首（尾）帧模式下生成视频比例参考图片，不传比例
                AspectRatio: frameMode === 'first' || frameMode === 'first_last'
                    ? undefined
                    : clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
                // 是否生成音频
                AudioGeneration: typeof providerOptions?.sound === 'boolean'
                    ? (providerOptions.sound === true ? 'Enabled' : 'Disabled')
                    : 'Enabled',
            };

            // 首帧模式
            if (frameMode === 'first') {
                return {
                    ModelName: 'Kling',
                    ModelVersion: '3.0',
                    Prompt: prompt.slice(0, 1000),
                    NegativePrompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 1000) : undefined,
                    EnhancePrompt: 'Disabled',
                    // 首帧 URL
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    OutputConfig: outputConfig,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    ModelName: 'Kling',
                    ModelVersion: '3.0',
                    Prompt: prompt.slice(0, 1000),
                    NegativePrompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 1000) : undefined,
                    EnhancePrompt: 'Disabled',
                    // 首帧 URL
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    // 尾帧 URL
                    LastFrameUrl: lastImage,
                    OutputConfig: outputConfig,
                };
            // 文生视频模式
            } else {
                return {
                    ModelName: 'Kling',
                    ModelVersion: '3.0',
                    Prompt: prompt.slice(0, 1000),
                    NegativePrompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 1000) : undefined,
                    EnhancePrompt: 'Disabled',
                    OutputConfig: outputConfig,
                };
            }
        },
    },
    'kling-v2.6': {
        enableTranslation: false,
        statusMapping: TENCENT_AIGC_STATUS_MAPPING,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);

            const outputConfig = {
                StorageMode: 'Temporary',
                Duration: clampTo(duration || 5, [5, 10] as const, 5),
                Resolution: '1080P',
                AspectRatio: frameMode === 'first' || frameMode === 'first_last'
                    ? undefined
                    : clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
                AudioGeneration: typeof providerOptions?.sound === 'boolean'
                    ? (providerOptions.sound === true ? 'Enabled' : 'Disabled')
                    : 'Enabled',
            };

            // 首帧模式
            if (frameMode === 'first') {
                return {
                    ModelName: 'Kling',
                    ModelVersion: '2.6',
                    Prompt: prompt.slice(0, 1000),
                    NegativePrompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 1000) : undefined,
                    EnhancePrompt: 'Disabled',
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    OutputConfig: outputConfig,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    ModelName: 'Kling',
                    ModelVersion: '2.6',
                    Prompt: prompt.slice(0, 1000),
                    NegativePrompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 1000) : undefined,
                    EnhancePrompt: 'Disabled',
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    LastFrameUrl: lastImage,
                    OutputConfig: outputConfig,
                };
            // 文生视频模式
            } else {
                return {
                    ModelName: 'Kling',
                    ModelVersion: '2.6',
                    Prompt: prompt.slice(0, 1000),
                    NegativePrompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 1000) : undefined,
                    EnhancePrompt: 'Disabled',
                    OutputConfig: outputConfig,
                };
            }
        },
    },
    'kling-v2.5': {
        enableTranslation: false,
        statusMapping: TENCENT_AIGC_STATUS_MAPPING,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);

            const outputConfig = {
                StorageMode: 'Temporary',
                Duration: clampTo(duration || 5, [5, 10] as const, 5),
                Resolution: clampTo(quality || '720p', ['720p', '1080p'] as const, '720p').toUpperCase(),
                AspectRatio: frameMode === 'first' || frameMode === 'first_last'
                    ? undefined
                    : clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
            };

            // 首帧模式
            if (frameMode === 'first') {
                return {
                    ModelName: 'Kling',
                    ModelVersion: '2.5',
                    Prompt: prompt.slice(0, 1000),
                    NegativePrompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 1000) : undefined,
                    EnhancePrompt: 'Disabled',
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    OutputConfig: outputConfig,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    ModelName: 'Kling',
                    ModelVersion: '2.5',
                    Prompt: prompt.slice(0, 1000),
                    NegativePrompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 1000) : undefined,
                    EnhancePrompt: 'Disabled',
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    LastFrameUrl: lastImage,
                    OutputConfig: outputConfig,
                };
            // 文生视频模式
            } else {
                return {
                    ModelName: 'Kling',
                    ModelVersion: '2.5',
                    Prompt: prompt.slice(0, 1000),
                    NegativePrompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 1000) : undefined,
                    EnhancePrompt: 'Disabled',
                    OutputConfig: outputConfig,
                };
            }
        },
    },
};
